import { TGetOrderResponse } from '@p2p/services';
import { toUTF8BinaryString, toWindows1251BinaryString } from '@p2p/shared/functions';

const MIN_RANDOM_VALUE = 10_000_000;
const MAX_RANDOM_VALUE = 9_999_999_999;

// P2P-2580: длина идентификатора после «#» в назначении платежа Сбера не должна превышать
// 10 символов (требование образца из ТЗ). generateRandomId и так укладывается в 8–10 цифр,
// но обрезаем на всякий случай, если источник id окажется длиннее.
const MAX_PURPOSE_ID_LENGTH = 10;

export const generateRandomId = (): string => {
  return `${Math.floor(Math.random() * (MAX_RANDOM_VALUE - MIN_RANDOM_VALUE + 1)) + MIN_RANDOM_VALUE}`;
};

type FieldConfig = {
  key: string;
  getValue: (order: TGetOrderResponse) => string | number | undefined;
};

// Для IronPay-реквизитов (qr_invoice) провайдер сам формирует полное персональное
// назначение с числом и возвращает его в order.purpose. Его нужно использовать целиком,
// без генерации собственного случайного номера. В остальных случаях используем
// назначение реквизита и добавляем случайный номер.
const hasPersonalPurpose = (order: TGetOrderResponse): boolean =>
  Boolean(order.requisite.qr_invoice && order.purpose);

// P2P-2552: qr_invoice-реквизиты, выданные клиентам Сбера, генерируются по отдельному
// шаблону (см. sberFieldConfigs + SBER_CODING). Сравнение регистронезависимое — бэк
// присылает client_bank_code в разном регистре ('sber'/'SBER'), как и в остальных
// проверках Сбера в проекте (OpenBankAppButton, InstructionInvoice, deeplink.utils).
export const isSberQrInvoice = (order: TGetOrderResponse): boolean =>
  Boolean(order.requisite.qr_invoice) && order.client_bank_code?.toLowerCase() === 'sber';

// P2P-2552: часть реквизитов приходит с бэка уже «моджибейком» — байты UTF-8, ошибочно
// прочитанные как Latin-1 (напр. «Индивидуальный» -> «Ð˜Ð½Ð´Ð¸Ð²Ð¸Ð´ÑƒÐ°Ð»ÑŒÐ½Ñ‹Ð¹»).
// Если такую строку ещё раз закодировать в UTF-8 (ST00012), в QR попадает ДВОЙНОЙ UTF-8,
// а под ST00011 такие символы не мапятся в Windows-1251 -> «?». Чиним ДО кодирования: если
// строка целиком в диапазоне 0x00–0xFF и является валидным UTF-8, декодируем её обратно в
// исходный Unicode. Настоящую кириллицу (кодпоинты > 0xFF) и невалидные последовательности
// не трогаем — чистые/ASCII-данные проходят без изменений.
const repairDoubleEncodedUtf8 = (value: string): string => {
  for (const ch of value) {
    if ((ch.codePointAt(0) ?? 0) > 0xff) {
      return value;
    }
  }

  try {
    const bytes = Uint8Array.from(value, (ch) => ch.charCodeAt(0));

    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return value;
  }
};

const FIELDS: Record<string, FieldConfig> = {
  Name: { key: 'Name', getValue: (order) => order.requisite.name },
  PersonalAcc: { key: 'PersonalAcc', getValue: (order) => order.requisite.account_number },
  BankName: { key: 'BankName', getValue: (order) => order.requisite.bank.name },
  BIC: { key: 'BIC', getValue: (order) => order.requisite.bic },
  CorrespAcc: { key: 'CorrespAcc', getValue: (order) => order.requisite.corresp_acc },
  Sum: { key: 'Sum', getValue: (order) => Math.floor(order.expected_income * 100) },
  SumRub: { key: 'SumRub', getValue: (order) => Math.floor(order.expected_income) },
  Purpose: {
    key: 'Purpose',
    getValue: (order) => (hasPersonalPurpose(order) ? order.purpose : order.requisite.purpose),
  },
  PayeeINN: { key: 'PayeeINN', getValue: (order) => order.requisite.payee_inn },
};

const fieldConfigs: FieldConfig[] = [
  FIELDS.Name,
  FIELDS.PersonalAcc,
  FIELDS.BankName,
  FIELDS.BIC,
  FIELDS.CorrespAcc,
  FIELDS.Sum,
  FIELDS.SumRub,
  FIELDS.Purpose,
  FIELDS.PayeeINN,
];

// P2P-2580: строго фиксированный шаблон QR для Сбера (ГОСТ Р 56042-2014) по образцу из ТЗ:
// ST00011|Name|PersonalAcc|BankName|BIC|CorrespAcc|PayeeINN|Sum|Purpose
// — PayeeINN идёт перед Sum, поле SumRub НЕ включается, поле KPP НЕ включается ни в каком виде.
const sberFieldConfigs: FieldConfig[] = [
  FIELDS.Name,
  FIELDS.PersonalAcc,
  FIELDS.BankName,
  FIELDS.BIC,
  FIELDS.CorrespAcc,
  FIELDS.PayeeINN,
  FIELDS.Sum,
  FIELDS.Purpose,
];

// Service tag ST0001 + encoding indicator (ГОСТ Р 56042): 1 = Windows-1251, 2 = UTF-8.
// Windows-1251 используется по умолчанию, чтобы кириллица оставалась однобайтной и байт 0xA0
// (который часть банковских сканеров превращает в пробел, теряя букву «Р») не появлялся.
// P2P-2547: если провайдер (например, IronPay) прислал свой служебный тег в order.coding —
// используем его как заголовок, чтобы кодировка совпадала с QR провайдера. Иначе — дефолт.
const DEFAULT_CODING = 'ST00011';

// P2P-2580: служебный тег для Сбера — ST00011 (как требует ТЗ). Но БАЙТЫ содержимого QR
// кодируем в UTF-8 (см. isUtf8 в generateQRCodeValue). Причина: приложение Сбера читает
// содержимое платёжного QR как UTF-8 и ИГНОРИРУЕТ индикатор кодировки в теге. Подтверждено
// живым сканом (заявка 26f3621a): Win-1251-байты Сбер декодирует как UTF-8 → «?»; UTF-8-байты
// читаются корректно; тот же UTF-8-контент с тегом ST00011 из стороннего генератора
// (code-qr.ru) тоже читается верно. order.coding провайдера для Сбера игнорируется.
const SBER_CODING = 'ST00011';

// P2P-2580: сборка «сырой» строки qr_data (Unicode, ДО байтовой кодировки). Вынесена отдельно,
// потому что для Сбера QR рендерится не через react-qr-code, а через @nuintun/qrcode с
// Charset.UTF_8 (ECI-26) — ей нужна именно сырая строка (см. QRCodeInvoice.tsx).
export const buildQrData = (order: TGetOrderResponse, randomId: string): string => {
  const isSber = isSberQrInvoice(order);
  const coding = isSber ? SBER_CODING : order.coding || DEFAULT_CODING;
  const configs = isSber ? sberFieldConfigs : fieldConfigs;
  const parts = [coding];

  for (const config of configs) {
    let value = config.getValue(order);

    if (value && config.key === 'Purpose' && !hasPersonalPurpose(order)) {
      const purposeId = isSber ? randomId.slice(0, MAX_PURPOSE_ID_LENGTH) : randomId;
      value = `${value} #${purposeId}`;
    }

    if (value !== undefined && value !== null && value !== '') {
      const safeValue = typeof value === 'string' ? repairDoubleEncodedUtf8(value) : value;

      parts.push(`${config.key}=${safeValue}`);
    }
  }

  return parts.join('|');
};

// Байтовая строка для react-qr-code (не-Сбер и EMVCo-фолбэк). Кодировка байтов совпадает с
// индикатором в теге (X=2 → UTF-8, иначе Windows-1251). Для Сбера рендер идёт через @nuintun
// (ECI-26), а не через это значение.
//
// P2P-2597 REVERTED: пробовали всегда форсировать Windows-1251-байты для не-Сбера (игнорируя
// coding.endsWith('2')), рассуждая, что react-qr-code без ECI читается сканером как Latin-1/1251
// и что это и есть причина «Р» → 2 пробела на чеке Alfa. На проде это сломало Alfa НАЦЕЛО —
// «Неизвестный тип платежа», сканер вообще отказался распознавать QR. Вероятная причина: тег в
// строке (buildQrData берёт его из order.coding = 'ST00012' у IronPay) остаётся заявлен как
// UTF-8, а байты стали Windows-1251 — Alfa, похоже, честно декодирует по тегу и на байтах,
// невалидных как UTF-8, просто не парсит QR целиком (в отличие от Сбера, который тег игнорирует
// и всегда лезет в UTF-8). Тег и байты должны совпадать → откатили к
// isSberQrInvoice(order) || coding.trim().endsWith('2'). Original «Р» → 2 пробела на чеке Alfa
// (P2P-2597) остаётся невылеченным, но это мягче полного отказа парсить QR — чинить нужно другим
// способом (не форсированием 1251), см. P2P-2597 в Jira: нужен живой тест сканом в Alfa.
// P2P-2846: часть провайдеров присылает УЖЕ ГОТОВУЮ строку платёжного QR, и её
// нельзя ни собирать заново, ни отдавать в react-qr-code как обычный текст.
//
// У YouTrade (Альфа QR) в настройках магазина выбран формат выдачи «сырые
// данные» — приходит ГОСТ-строка вида "ST00012|Name=...|PersonalAcc=...", с
// кириллицей внутри. react-qr-code кодирует значение побайтово (по одному байту
// на символ), поэтому кириллицу нужно заранее превратить в байты той кодировки,
// которую объявляет служебный тег: X=2 — UTF-8, иначе Windows-1251. Тег и байты
// обязаны совпадать — рассинхрон уже ломал сканирование в Alfa насмерть
// («Неизвестный тип платежа», P2P-2597), поэтому правило здесь ровно то же, что
// и в generateQRCodeValue ниже.
//
// EMVCo-payload (SimplePay, BayPay) — чистый ASCII без тега ГОСТ, для него
// преобразование не нужно и не делается: возвращаем строку как есть.
const GOST_QR_PREFIX = 'ST0001';

export const isGostQrPayload = (payload: string): boolean =>
  payload.trimStart().startsWith(GOST_QR_PREFIX);

export const encodeReadyQrPayload = (payload: string): string => {
  if (!isGostQrPayload(payload)) {
    return payload;
  }

  const separatorIndex = payload.indexOf('|');
  const coding = separatorIndex === -1 ? payload : payload.slice(0, separatorIndex);

  return coding.trim().endsWith('2')
    ? toUTF8BinaryString(payload)
    : toWindows1251BinaryString(payload);
};

export const generateQRCodeValue = (order: TGetOrderResponse, randomId: string): string => {
  const qrData = buildQrData(order, randomId);
  const coding = qrData.slice(0, qrData.indexOf('|'));
  const isUtf8 = isSberQrInvoice(order) || coding.trim().endsWith('2');

  return isUtf8 ? toUTF8BinaryString(qrData) : toWindows1251BinaryString(qrData);
};
